import React from 'react'
import { Store } from './Store'
import LoansList from './LoansList'
import { EllipsisLoader } from '@components/Loaders'
import { formatCurrency } from '@utilities/numbers'
import Carousel from '@components/Carousel'
import { sanitize } from 'dompurify'
import RegionalData from '../lenders/views/Property/RegionalData'

import TotalFundingChart from '@components/TotalFundingChart'

const loanAgreements = ['Fixed Loan Agreement', 'Equity Loan Agreement', 'Tenant Loan Agreement']
const documentOrder = ['RICS Homebuyers Report and Valuation', 'Hometrack Valuation']

export default function PropertyPage(props: any) {
  // @ts-ignore
  const [state, dispatch] = React.useContext(Store)
  let property = state.properties[props.propertyId]

  React.useEffect(() => {
    dispatch({ type: 'REQUIRE_PROPERTY_DETAILS', propertyId: props.propertyId })
  }, [state.properties])

  // Lender loans
  let sharedEquityLoan: any
  let firstChargeMortgage: any
  let tenantEquityLoan: any

  if (property && property.loans) {
    sharedEquityLoan = property?.loans?.filter(
      (l: any) => l.principal_repayment_type === 'Equity repayment'
    )[0]
    firstChargeMortgage = property?.loans?.filter(
      (l: any) => l.principal_repayment_type === 'Fixed repayment'
    )[0]
    tenantEquityLoan =
      property?.total_cost -
      (sharedEquityLoan.amount_borrowed + firstChargeMortgage.amount_borrowed)
  }

  function contentsForMode(mode: string) {
    switch (mode) {
    case 'Error': {
      return <div>Error: {state.errorMessage}</div>
    }
    default: {
      if (!property)
        return (
            <div className="-mod-margin-top">
              <EllipsisLoader />
            </div>
        )

      const floorplanDoc = property?.documents?.find((doc: any) => doc.name === 'floorplan')
      const otherDocs = property?.documents
          ?.filter((doc: any) => {
            return doc.name !== 'floorplan' && !loanAgreements.includes(doc.display_name)
          })
          .sort((a: any, b: any) => {
            let indexA = documentOrder.indexOf(a.display_name)
            let indexB = documentOrder.indexOf(b.display_name)
            if (indexA === -1) {
              indexA = 1000
            }
            if (indexB === -1) {
              indexB = 1000
            }
            return indexA - indexB
          })

      return (
          <>
            <div className="Section -mod-spacing-small">
              <h1 className="Heading -two -mod-margin-bottom">
                {property?.company_name} ({property?.posttown}, {property?.postcode})
              </h1>
              <div
                className="Text -large -mod-wrap-wide"
                dangerouslySetInnerHTML={{
                  __html: sanitize(property?.investment_summary)
                }}
              ></div>

              <ul className="Columns -spaced -mod-margin-top-3">
                <li>
                  <Carousel childSelector="picture">
                    {(property?.photos ?? []).map((photo: any) => (
                      <picture
                        key={photo.url}
                        className="Picture -cover -mod-margin-top  -mod-margin-bottom"
                      >
                        <img src={photo.url} alt="Property image" />
                      </picture>
                    ))}
                  </Carousel>
                </li>
                <li>
                  <ul className="List -mod-margin-bottom -mod-margin-top">
                    <li key={floorplanDoc?.url} className="-">
                      <a
                        className="Button -fullwidth -icon-left"
                        href={floorplanDoc?.url}
                        download={property?.full_address + '___' + floorplanDoc?.display_name}
                      >
                        <i className="Icon fas fa-file -light" style={{ color: '#BBB' }} /> &nbsp;
                        {floorplanDoc?.display_name}
                      </a>
                    </li>
                  </ul>

                  <div className="Inaccessible -mod-margin-bottom-2">
                    <ul className="List">
                      {otherDocs?.map((doc: any) => (
                        <li key={doc.url} className="-">
                          <span className="Button -fullwidth -disabled -icon-left">
                            <i className="Icon fas fa-file -light" style={{ color: '#888' }} />{' '}
                            &nbsp;
                            {doc.display_name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="Row -spaced -gt-mobile">
                    <a href="/investing/product-withdrawn/" className="Button -large -primary">
                      Sign up
                    </a>
                    <div>to view valuation documents</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="Section -mod-spacing-small">
              <h2 className="Heading -two -underline">Property description</h2>
              <ul className="Columns -spaced">
                <li>
                  <div
                    className="Content -mod-wrap-wide -mod-margin-top -mod-margin-bottom"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(property?.description)
                    }}
                  />
                </li>
                <li>
                  <RegionalData postcode={property?.postcode} />
                </li>
              </ul>
            </div>

            <div className="Section -mod-spacing-small">
              <h2 className="Heading -two -underline">Tenant description</h2>
              <div
                className="Content -mod-wrap-wide -mod-margin-top -mod-margin-bottom"
                dangerouslySetInnerHTML={{
                  __html: sanitize(property?.tenant_description)
                }}
              />
            </div>
            <div className="Section -mod-wrap-widest -mod-spacing-small">
              <h2 className="Heading -two -underline">
                Total funding: {formatCurrency(Number(property?.total_cost))}
              </h2>
              {tenantEquityLoan && sharedEquityLoan && firstChargeMortgage ? (
                <TotalFundingChart
                  repairProvisions={parseInt(property?.repair_provisions || 0, 10)}
                  fees={parseInt(property?.other_fees || 0, 10)}
                  stampDuty={parseInt(property?.stamp_duty || 0, 10)}
                  purchasePrice={parseInt(property?.purchase_price || 0, 10)}
                  tenantEquityLoan={tenantEquityLoan}
                  standardEquityLoan={sharedEquityLoan.amount_borrowed}
                  firstChargeMortgage={firstChargeMortgage.amount_borrowed}
                />
              ) : null}
            </div>

            <LoansList
              loanDocs={property?.documents}
              loans={property.loans ? property.loans : []}
            />
          </>
      )
    }
    }
  }

  return (
    <div className="Wrapper -wide -mod-margin-bottom -mod-margin-top">
      <div className="Section -mod-spacing-small-bottom"></div>
      {contentsForMode(state.mode)}
    </div>
  )
}
